import {
  type InfiniteData,
  type QueryFunctionContext,
  type UseInfiniteQueryResult,
  useInfiniteQuery,
} from '@tanstack/react-query';

import { roleService } from '@/services/role-service';
import type { Role } from '@/types/core/role';
import type { Page } from '@/types/shared/page';
import { isEmptyUUID, type UUID } from '@/utils/uuid';

import { roleQueryKeys } from './query-keys';

/**
 * List roles for a given tenant.
 * Accepts optional paging args via `opts`.
 */
export function useListRoles(
  tenantId: UUID,
  opts?: {
    limit?: number;
    next?: string;
    filter?: string;
  }
): UseInfiniteQueryResult<InfiniteData<Page<Role>, unknown>, Error> {
  return useInfiniteQuery<Page<Role>, Error, InfiniteData<Page<Role>, unknown>>(
    {
      queryKey: roleQueryKeys.list(tenantId, opts?.limit, opts?.filter),
      queryFn: (context: QueryFunctionContext) => {
        const pageParam = context.pageParam as string | undefined;
        return roleService.listRoles(tenantId, {
          limit: opts?.limit,
          next: pageParam,
          filter: opts?.filter,
        });
      },
      enabled: !isEmptyUUID(tenantId),
      initialPageParam: opts?.next ?? undefined,
      getNextPageParam: (last) => last.next ?? undefined,
      getPreviousPageParam: (first) => first.prev ?? undefined,
    }
  );
}
